import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import axios from "axios";
import { Grid, Pagination, Rating } from "@mui/material";
import { MoonLoader } from "react-spinners";
import { checkLoggedIn } from "../../utils";
import DeleteIcon from "../../icons/DeleteIcon";
import { useLoginModal } from "../../Context/LoginModalContext";

const DealProduct = ({
  addToCartProduct,
  addToWishListProduct,
  addToCompareListProduct,
  ...props
}) => {
  const [dealZone, setDealZone] = useState([]);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 6,
    totalRecords: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const sectionRef = useRef(null);

  const handlePageChange = (event, value) => {
    setFilter((prev) => ({ ...prev, page: value }));
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const fetchDeals = () => {
    setLoading(true);
    if (error) setError(false);
    axios
      .get("get-products", {
        params: { dealzone: true, ...filter },
      })
      .then((response) => {
        const data = response?.data?.data || [];
        setDealZone(data.map((el) => ({ ...el, id: el._id })));
        setFilter((prev) => ({
          ...prev,
          totalRecords: response?.data?.totalRecords,
        }));
      })
      .catch(() => {
        setDealZone([]);
        setError(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.page]);

  const totalRecords = Number(filter?.totalRecords) || 0;
  const limit = Number(filter?.limit) || 6;
  const pageCount = Math.max(1, Math.ceil(totalRecords / limit));

  return (
    <section className="gpdp-section" ref={sectionRef}>
      <div className="gpdp-bg-orb gpdp-bg-orb-tl" />
      <div className="gpdp-bg-orb gpdp-bg-orb-br" />
      <div className="gpdp-bg-grid" aria-hidden="true" />

      <div className="gpdp-inner">
        {/* HEADER */}
        <div className="gpdp-head">
          <div className="gpdp-head-text">
            <div className="gpdp-eyebrow">
              <span className="gpdp-eyebrow-dot" />
              🔥 LIMITED TIME · DEAL ZONE
            </div>
            <h2 className="gpdp-title">
              Today's <span className="gpdp-title-accent">Hot Deals</span>
            </h2>
            <p className="gpdp-sub">
              Hand-picked offers updated daily. Lock in premium gift cards at
              their best prices — while stocks last.
            </p>
          </div>

          <div className="gpdp-head-right">
            {!loading && !error && totalRecords > 0 && (
              <div className="gpdp-meta-pill">
                <strong>{totalRecords}</strong> active deals
              </div>
            )}
            <Link to="/shop" className="gpdp-viewall">
              View all deals
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
          </div>
        </div>

        {/* GRID */}
        {loading ? (
          <div className="gpdp-grid">
            {Array.from({ length: filter?.limit || 6 }).map((_, i) => (
              <div key={i} className="gpdp-skel">
                <div className="gpdp-skel-img" />
                <div className="gpdp-skel-body">
                  <div className="gpdp-skel-line gpdp-skel-line-lg" />
                  <div className="gpdp-skel-line gpdp-skel-line-md" />
                  <div className="gpdp-skel-line gpdp-skel-line-sm" />
                  <div className="gpdp-skel-line gpdp-skel-line-md" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="gpdp-empty">
            <div className="gpdp-empty-icon" aria-hidden="true">⚠</div>
            <h3>Couldn't load deals</h3>
            <p>Please check your connection and try again.</p>
            <button
              type="button"
              className="gpdp-empty-btn"
              onClick={fetchDeals}
            >
              Try again
            </button>
          </div>
        ) : dealZone.length === 0 ? (
          <div className="gpdp-empty">
            <div className="gpdp-empty-icon" aria-hidden="true">🏷</div>
            <h3>No active deals right now</h3>
            <p>Fresh promotions go live every week — stay tuned.</p>
            <Link to="/shop" className="gpdp-empty-btn">
              Browse all gift cards
            </Link>
          </div>
        ) : (
          <>
            <div className="gpdp-grid">
              {dealZone.map((product, index) => (
                <div
                  key={product?.id || index}
                  className="gpdp-card-wrap"
                  style={{ animationDelay: `${Math.min(index * 60, 480)}ms` }}
                >
                  <div className="gpdp-card-glow" aria-hidden="true" />
                  <DealZoneCard
                    product={product}
                    addToCartProduct={addToCartProduct}
                    {...props}
                  />
                </div>
              ))}
            </div>

            {pageCount > 1 && (
              <div className="gpdp-pagination">
                <Pagination
                  count={pageCount}
                  size="medium"
                  page={filter?.page || 1}
                  onChange={handlePageChange}
                  siblingCount={1}
                  shape="rounded"
                />
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        /* ============ SECTION ============ */
        .gpdp-section {
          position: relative;
          background: linear-gradient(180deg, #0b0b0c 0%, #07060a 100%);
          padding: 70px 22px 80px;
          overflow: hidden;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          color: #fff;
          border-top: 1px solid rgba(212, 168, 87, 0.08);
        }
        .gpdp-bg-orb {
          position: absolute;
          width: 380px;
          height: 380px;
          border-radius: 50%;
          filter: blur(120px);
          pointer-events: none;
          opacity: 0.55;
        }
        .gpdp-bg-orb-tl {
          background: rgba(212, 168, 87, 0.18);
          top: -140px;
          left: -100px;
        }
        .gpdp-bg-orb-br {
          background: rgba(212, 168, 87, 0.12);
          bottom: -160px;
          right: -100px;
        }
        .gpdp-bg-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(212, 168, 87, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(212, 168, 87, 0.04) 1px, transparent 1px);
          background-size: 48px 48px;
          mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
          -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
          pointer-events: none;
          opacity: 0.5;
        }

        .gpdp-inner {
          position: relative;
          max-width: 1300px;
          margin: 0 auto;
          z-index: 1;
        }

        /* ============ HEADER ============ */
        .gpdp-head {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 18px;
          margin-bottom: 36px;
          flex-wrap: wrap;
        }
        .gpdp-head-text { max-width: 720px; }
        .gpdp-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px;
          background: rgba(212, 168, 87, 0.1);
          border: 1px solid rgba(212, 168, 87, 0.3);
          color: #d4a857;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 2px;
          margin-bottom: 14px;
        }
        .gpdp-eyebrow-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #d4a857;
          box-shadow: 0 0 10px #d4a857;
          animation: gpdp-pulse 1.6s ease-in-out infinite;
        }
        @keyframes gpdp-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(0.85); }
        }
        .gpdp-title {
          font-family: "Playfair Display", "Times New Roman", serif;
          font-size: 38px;
          font-weight: 700;
          color: #fff;
          margin: 0 0 8px;
          line-height: 1.15;
          letter-spacing: -0.4px;
        }
        .gpdp-title-accent {
          background: linear-gradient(135deg, #f1cf85, #b58a3f);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          font-style: italic;
          font-weight: 600;
        }
        .gpdp-sub {
          font-size: 14.5px;
          color: #9aa0a8;
          line-height: 1.55;
          margin: 0;
          max-width: 580px;
        }

        .gpdp-head-right {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        .gpdp-meta-pill {
          padding: 8px 14px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(212, 168, 87, 0.2);
          color: #cfd2d6;
          border-radius: 999px;
          font-size: 12px;
        }
        .gpdp-meta-pill strong { color: #d4a857; font-weight: 700; margin-right: 4px; }

        .gpdp-viewall {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 11px 18px;
          background: linear-gradient(135deg, #f1cf85 0%, #d4a857 50%, #b58a3f 100%);
          color: #1a1208 !important;
          border: 1px solid rgba(255, 232, 178, 0.5);
          border-radius: 999px;
          font-size: 12.5px;
          font-weight: 700;
          letter-spacing: 0.6px;
          text-decoration: none;
          box-shadow: 0 6px 16px rgba(212, 168, 87, 0.3);
          transition: transform .25s ease, box-shadow .25s ease, filter .25s ease;
        }
        .gpdp-viewall:hover {
          color: #1a1208 !important;
          text-decoration: none;
          transform: translateY(-1px);
          filter: brightness(1.05);
          box-shadow: 0 10px 22px rgba(212, 168, 87, 0.45);
        }
        .gpdp-viewall svg { transition: transform .2s ease; }
        .gpdp-viewall:hover svg { transform: translateX(2px); }

        /* ============ GRID (2-col horizontal cards) ============ */
        .gpdp-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 22px;
        }
        .gpdp-card-wrap {
          position: relative;
          opacity: 0;
          transform: translateY(14px);
          animation: gpdp-fadeup .55s cubic-bezier(0.22, 1, 0.36, 1) forwards;
          transition: transform .35s cubic-bezier(0.22, 1, 0.36, 1);
          border-radius: 16px;
        }
        .gpdp-card-wrap:hover { transform: translateY(-6px); }
        @keyframes gpdp-fadeup {
          to { opacity: 1; transform: translateY(0); }
        }
        .gpdp-card-glow {
          position: absolute;
          inset: -2px;
          border-radius: 18px;
          background: linear-gradient(135deg, rgba(212, 168, 87, 0.6), rgba(180, 130, 40, 0));
          opacity: 0;
          transition: opacity .3s ease;
          pointer-events: none;
          z-index: 0;
          filter: blur(8px);
        }
        .gpdp-card-wrap:hover .gpdp-card-glow { opacity: 0.55; }

        /* ============ HORIZONTAL CARD ============ */
        .gpdp-card {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: 180px 1fr;
          gap: 16px;
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px solid rgba(212, 168, 87, 0.18);
          border-radius: 16px;
          overflow: hidden;
          padding: 14px;
          box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
          transition: border-color .3s ease, box-shadow .3s ease;
          height: 100%;
          min-height: 200px;
        }
        .gpdp-card-wrap:hover .gpdp-card {
          border-color: rgba(212, 168, 87, 0.55);
          box-shadow: 0 18px 38px rgba(212, 168, 87, 0.18), 0 6px 14px rgba(0, 0, 0, 0.4);
        }

        .gpdp-card-img {
          position: relative;
          background: #0b0b0c;
          border: 1px solid rgba(212, 168, 87, 0.12);
          border-radius: 12px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          aspect-ratio: 1 / 1;
        }
        .gpdp-card-img img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform .5s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .gpdp-card-wrap:hover .gpdp-card-img img {
          transform: scale(1.05);
        }
        .gpdp-card-img-fallback {
          color: rgba(212, 168, 87, 0.4);
          font-size: 32px;
        }
        .gpdp-card-deal-badge {
          position: absolute;
          top: 8px;
          left: 8px;
          background: linear-gradient(135deg, #ff6b6b, #c0392b);
          color: #fff;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.6px;
          padding: 4px 8px;
          border-radius: 999px;
          box-shadow: 0 4px 10px rgba(192, 57, 43, 0.4);
          z-index: 2;
        }

        .gpdp-card-body {
          display: flex;
          flex-direction: column;
          gap: 8px;
          min-width: 0;
        }
        .gpdp-card-title {
          font-size: 15px;
          font-weight: 700;
          line-height: 1.3;
          margin: 0;
        }
        .gpdp-card-title a {
          color: #fff !important;
          text-decoration: none;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .gpdp-card-title a:hover {
          color: #d4a857 !important;
          text-decoration: none;
        }
        .gpdp-card-rating {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #cfd2d6;
          font-size: 12px;
        }
        .gpdp-card-rating-num { color: #d4a857; font-weight: 600; }
        .gpdp-card-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 2px;
        }
        .gpdp-card-meta span {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(212, 168, 87, 0.18);
          color: #9aa0a8;
          padding: 3px 9px;
          border-radius: 999px;
          font-size: 11px;
          line-height: 1.3;
        }
        .gpdp-card-meta span strong {
          color: #cfd2d6;
          font-weight: 600;
          margin-right: 4px;
        }

        .gpdp-card-foot {
          margin-top: auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding-top: 10px;
          border-top: 1px solid rgba(212, 168, 87, 0.12);
        }
        .gpdp-card-price-wrap {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .gpdp-card-price-label {
          font-size: 10px;
          color: #6b7280;
          letter-spacing: 0.6px;
          text-transform: uppercase;
        }
        .gpdp-card-price {
          color: #d4a857;
          font-size: 18px;
          font-weight: 800;
          line-height: 1;
        }
        .gpdp-card-cart {
          background: linear-gradient(135deg, #f1cf85 0%, #d4a857 50%, #b58a3f 100%);
          color: #1a1208;
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 10px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          box-shadow: 0 6px 14px rgba(212, 168, 87, 0.3);
          transition: transform .2s ease, filter .2s ease;
        }
        .gpdp-card-cart:hover:not(:disabled) {
          transform: translateY(-1px);
          filter: brightness(1.05);
        }
        .gpdp-card-cart:disabled { opacity: 0.7; cursor: not-allowed; }

        .gpdp-qty {
          display: inline-flex;
          align-items: center;
          gap: 0;
          background: rgba(0, 0, 0, 0.4);
          border: 1px solid rgba(212, 168, 87, 0.3);
          border-radius: 10px;
          overflow: hidden;
        }
        .gpdp-qty button {
          background: transparent;
          border: none;
          color: #d4a857;
          width: 32px;
          height: 38px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background .15s ease;
        }
        .gpdp-qty button:hover:not(:disabled) {
          background: rgba(212, 168, 87, 0.15);
        }
        .gpdp-qty button:disabled { opacity: 0.6; cursor: not-allowed; }
        .gpdp-qty-val {
          color: #fff;
          font-weight: 700;
          font-size: 13px;
          min-width: 26px;
          text-align: center;
          padding: 0 4px;
        }

        /* ============ SKELETON ============ */
        .gpdp-skel {
          display: grid;
          grid-template-columns: 180px 1fr;
          gap: 16px;
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px solid rgba(212, 168, 87, 0.12);
          border-radius: 16px;
          padding: 14px;
          min-height: 200px;
        }
        .gpdp-skel-img {
          aspect-ratio: 1 / 1;
          border-radius: 12px;
          background: linear-gradient(90deg, #1a1208 0%, #2a1f10 50%, #1a1208 100%);
          background-size: 200% 100%;
          animation: gpdp-shimmer 1.4s linear infinite;
        }
        .gpdp-skel-body {
          display: flex;
          flex-direction: column;
          gap: 10px;
          justify-content: center;
        }
        .gpdp-skel-line {
          height: 12px;
          border-radius: 4px;
          background: linear-gradient(90deg, #1a1208 0%, #2a1f10 50%, #1a1208 100%);
          background-size: 200% 100%;
          animation: gpdp-shimmer 1.4s linear infinite;
        }
        .gpdp-skel-line-lg { height: 16px; width: 80%; }
        .gpdp-skel-line-md { width: 55%; }
        .gpdp-skel-line-sm { width: 35%; }
        @keyframes gpdp-shimmer {
          from { background-position: 200% 0; }
          to { background-position: -200% 0; }
        }

        /* ============ EMPTY / ERROR ============ */
        .gpdp-empty {
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px dashed rgba(212, 168, 87, 0.3);
          border-radius: 16px;
          padding: 56px 24px;
          text-align: center;
          max-width: 540px;
          margin: 0 auto;
        }
        .gpdp-empty-icon {
          font-size: 36px;
          color: #d4a857;
          margin-bottom: 12px;
        }
        .gpdp-empty h3 {
          font-size: 18px;
          color: #fff;
          margin: 0 0 6px;
          font-weight: 700;
        }
        .gpdp-empty p {
          color: #9aa0a8;
          margin: 0 0 20px;
          font-size: 13.5px;
        }
        .gpdp-empty-btn {
          display: inline-flex;
          align-items: center;
          background: linear-gradient(135deg, #d4a857, #b58a3f);
          color: #1a1208 !important;
          border: none;
          padding: 11px 22px;
          border-radius: 10px;
          font-weight: 700;
          font-size: 13px;
          letter-spacing: 0.5px;
          cursor: pointer;
          text-decoration: none;
          transition: transform .2s ease, filter .2s ease;
          font-family: inherit;
        }
        .gpdp-empty-btn:hover {
          transform: translateY(-2px);
          filter: brightness(1.05);
          color: #1a1208 !important;
          text-decoration: none;
        }

        /* ============ PAGINATION ============ */
        .gpdp-pagination {
          display: flex;
          justify-content: center;
          padding: 40px 0 0;
        }
        .gpdp-pagination .MuiPaginationItem-root {
          color: #cfd2d6 !important;
          font-weight: 600;
          border-radius: 10px;
          margin: 0 2px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(212, 168, 87, 0.15);
        }
        .gpdp-pagination .MuiPaginationItem-root:hover {
          background: rgba(212, 168, 87, 0.15) !important;
          color: #d4a857 !important;
          border-color: rgba(212, 168, 87, 0.4);
        }
        .gpdp-pagination .MuiPaginationItem-root.Mui-selected {
          background: linear-gradient(135deg, #d4a857, #b58a3f) !important;
          color: #1a1208 !important;
          border-color: transparent !important;
          box-shadow: 0 6px 14px rgba(212, 168, 87, 0.3);
        }
        .gpdp-pagination .MuiPaginationItem-root.Mui-selected:hover {
          background: linear-gradient(135deg, #f1cf85, #d4a857) !important;
        }

        /* ============ RESPONSIVE ============ */
        @media (max-width: 820px) {
          .gpdp-section { padding: 56px 18px 64px; }
          .gpdp-title { font-size: 28px; }
          .gpdp-sub { font-size: 13.5px; }
          .gpdp-grid { grid-template-columns: 1fr; gap: 16px; }
          .gpdp-card { grid-template-columns: 130px 1fr; gap: 12px; padding: 12px; min-height: 0; }
          .gpdp-skel { grid-template-columns: 130px 1fr; gap: 12px; padding: 12px; }
        }
        @media (max-width: 480px) {
          .gpdp-section { padding: 44px 14px 52px; }
          .gpdp-title { font-size: 22px; }
          .gpdp-eyebrow { font-size: 10px; letter-spacing: 1.5px; }
          .gpdp-card { grid-template-columns: 110px 1fr; gap: 10px; padding: 10px; }
          .gpdp-skel { grid-template-columns: 110px 1fr; gap: 10px; padding: 10px; }
          .gpdp-card-title { font-size: 13px; }
          .gpdp-card-price { font-size: 16px; }
          .gpdp-card-meta { gap: 4px; }
          .gpdp-card-meta span { font-size: 10px; padding: 2px 7px; }
          .gpdp-head { gap: 14px; flex-direction: column; align-items: flex-start; }
          .gpdp-head-right { width: 100%; }
          .gpdp-viewall { flex: 1; justify-content: center; }
        }
      `}</style>
    </section>
  );
};

export default DealProduct;

const DealZoneCard = ({
  product,
  addToWishListProduct,
  addToCartProduct,
  carts = [],
  removeFromCart,
  decrementQuantity,
  incrementQuantity,
  ...props
}) => {
  const [loading, setLoading] = useState(false);
  const { openLoginModal } = useLoginModal();

  const inCart = useMemo(
    () => carts.find((cart) => cart.id === product?.id),
    [carts, product]
  );

  const handleAddToCart = async () => {
    if (!checkLoggedIn()) {
      openLoginModal();
      return;
    }
    setLoading(true);
    await addToCartProduct(product);
    setLoading(false);
  };

  const handleIncrementQuantity = async () => {
    if (!checkLoggedIn()) {
      openLoginModal();
      return;
    }
    setLoading(true);
    await incrementQuantity(product.id);
    setLoading(false);
  };

  const handleDecrementQuantity = async () => {
    if (!checkLoggedIn()) {
      openLoginModal();
      return;
    }
    setLoading(true);
    if (inCart?.qty === 1) {
      await removeFromCart(product.id);
    } else {
      await decrementQuantity(product.id);
    }
    setLoading(false);
  };

  const img = product?.proimg?.[0]?.src;
  const ratingNum = Number(product?.five_start_rating) || 0;

  return (
    <article className="gpdp-card">
      <Link
        to={`/product-single/${product?.slug}`}
        className="gpdp-card-img"
        aria-label={product?.title}
      >
        <span className="gpdp-card-deal-badge">DEAL</span>
        {img ? (
          <img src={img} alt={product?.title || "deal"} />
        ) : (
          <span className="gpdp-card-img-fallback">★</span>
        )}
      </Link>

      <div className="gpdp-card-body">
        <h3 className="gpdp-card-title">
          <Link to={`/product-single/${product?.slug}`}>{product.title}</Link>
        </h3>

        <div className="gpdp-card-rating">
          <Rating
            name="read-only"
            precision={0.5}
            value={ratingNum}
            readOnly
            size="small"
            sx={{
              "& .MuiRating-iconFilled": { color: "#d4a857" },
              "& .MuiRating-iconEmpty": { color: "#3a2f1c" },
            }}
          />
          <span className="gpdp-card-rating-num">{ratingNum || 0}</span>
        </div>

        <div className="gpdp-card-foot">
          <div className="gpdp-card-price-wrap">
            <span className="gpdp-card-price-label">Deal price</span>
            <span className="gpdp-card-price">${product?.price}</span>
          </div>

          {inCart ? (
            <Grid className="gpdp-qty" container>
              <button
                onClick={handleDecrementQuantity}
                disabled={loading}
                aria-label="Decrease quantity"
              >
                {loading ? (
                  <MoonLoader size={12} color="#d4a857" />
                ) : inCart.qty === 1 ? (
                  <DeleteIcon />
                ) : (
                  "−"
                )}
              </button>
              <span className="gpdp-qty-val">{inCart?.qty}</span>
              <button
                onClick={handleIncrementQuantity}
                disabled={loading}
                aria-label="Increase quantity"
              >
                {loading ? <MoonLoader size={12} color="#d4a857" /> : "+"}
              </button>
            </Grid>
          ) : (
            <button
              type="button"
              className="gpdp-card-cart"
              data-tooltip-id="gpdp-cart-tip"
              data-tooltip-content="Add to cart"
              data-tooltip-place="left"
              onClick={handleAddToCart}
              disabled={loading}
              aria-label="Add to cart"
            >
              {loading ? (
                <MoonLoader size={14} color="#1a1208" />
              ) : (
                <i className="icon-cart" />
              )}
            </button>
          )}
          <Tooltip id="gpdp-cart-tip" />
        </div>
      </div>
    </article>
  );
};
